import { Timestamp } from './timestamp';
import { Clock, ClockService } from './clock.service';
import { MerkleTrie, insert } from './merkle';
import { Operation, OperationDTO, PartialOperation } from '../models/operation.model';
import { ExtraState, State } from '../models/state';
import { SalesDocument } from '../models/sales-document.model';
import { GroupingService } from '../services/grouping.service';
import { groupingSchemas } from '../models/grouping-schemas';
import { DocumentSummarize } from '../services/summarize.service';
import { core, logger, parseDate } from '@icc/shared/helpers';
import { operationFunctions, operationIndexFunc } from './operations';
import { TaxRatesService } from '../services/tax-rates.service';
import { DocumentStatusService } from '../services/document-status.service';
import { DocumentStatusSystems } from '../models/document-status.model';
import { Synced } from '../models/synced.model';
import { SalesDocumentPosition } from '../models/sales-document-position.model';
import { DocumentType } from '../models/document-type';
import { DocumentBuiltinFlagDict } from '../document-builtin-flags';

export function merge(operations: Operation[], state: State, clock: Clock, statusSystems?: DocumentStatusSystems, unavailableDocs?: string[], updateSynced = false,): State {
    state = filterUnavailableDocs(state, unavailableDocs);
    const extraState: ExtraState = {
        positionsByDocs: new Map(),
        statusSystems
    };
    for (const doc of Object.values(state.documents)) {
        let docPositions = extraState.positionsByDocs.get(doc.uuid);
        if (!docPositions) {
            docPositions = new Map();
        }
        extraState.positionsByDocs.set(doc.uuid, docPositions)
    }
    for (const pos of Object.values(state.positions)) {
        const docPositions = extraState.positionsByDocs.get(pos.documentId);
        if (docPositions) {
            docPositions.set(pos.uuid, pos);
            extraState.positionsByDocs.set(pos.documentId, docPositions)
        }
    }

    const savedIds = state.operationsIds;
    const opsToSaveIndex = new Map<string, number>();
    // console.log('BEFORE merge', operations.length && clock.merklesMap[operations[0].docId]?.hash);
    operations.forEach((op) => {
        let key = op.index || operationIndexFunc(op);
        let existingMsg = state.operationsKeysMap.get(key);
        if (!existingMsg || existingMsg.timestamp < op.timestamp) {
            state = apply(op, state, extraState);
        }
        if (!existingMsg || existingMsg.timestamp !== op.timestamp && !state.operationsIds.has(op.id)) {
            if (op.type === 'addDocMx') {
                if (!state.operationsIds.has(op.id)) {
                    putOperationInOrder(op, state.operations);
                    for (const docOp of op.ops) {
                        if (!state.operationsIds.has(docOp.id)) {
                            const parsedTimestamp = Timestamp.parse(docOp.timestamp);
                            if (parsedTimestamp) {
                                const oldHash = clock.merklesMap[docOp.docId]?.hash;
                                clock.merklesMap[docOp.docId] = insert(
                                    clock.merklesMap[docOp.docId] || {},
                                    parsedTimestamp,
                                    docOp.type
                                );
                                // logger.log('MERKLE INSERT', docOp.docId, docOp.timestamp, docOp.type, 's', 'mx', oldHash, clock.merklesMap[docOp.docId].hash);
                                ClockService.updatedGraphs[docOp.docId] = (ClockService.updatedGraphs[docOp.docId] || 0) + 1;
                                if (updateSynced) {
                                    state.synced.set(docOp.docId, Synced.Syncing);
                                }
                                if ('buyDocId' in docOp && docOp.buyDocId) {
                                    const oldHash = clock.merklesMap[docOp.buyDocId]?.hash;
                                    clock.merklesMap[docOp.buyDocId] = insert(
                                        clock.merklesMap[docOp.buyDocId] || {},
                                        parsedTimestamp,
                                        docOp.type
                                    );
                                    // logger.log('MERKLE INSERT', docOp.buyDocId, docOp.timestamp, docOp.type, 'b', 'mx', oldHash, clock.merklesMap[docOp.buyDocId].hash);
                                    ClockService.updatedGraphs[docOp.buyDocId] = (ClockService.updatedGraphs[docOp.buyDocId] || 0) + 1;
                                    if (updateSynced) {
                                        state.synced.set(docOp.buyDocId, Synced.Syncing);
                                    }
                                }
                            }
                            if (!opsToSaveIndex.has(docOp.id)) {
                                putOperationInOrder(docOp, state.operations);
                                const newLength = state.opsToSave.push(docOp as unknown as Operation);
                                opsToSaveIndex.set(docOp.id, newLength - 1);
                                key = docOp.index;
                                existingMsg = state.operationsKeysMap.get(key);
                                if (!existingMsg?.timestamp || existingMsg?.timestamp < docOp.timestamp) {
                                    state.operationsKeysMap.set(key, docOp);
                                }
                                state.opsToSendToWorker.push(docOp as unknown as Operation);
                            }
                            state.operationsIds.set(docOp.id, true);
                        } else {
                            // console.log('MERGE ADDDOCMX D', docOp.id, 'EXISTS', state.operationsIds.has(docOp.id), state.operationsKeysMap.get(key)?.timestamp, docOp.timestamp);
                        }
                    }
                    state.operationsIds.set(op.id, true);
                } else {
                    // console.log('MERGE ADDDOCMX', op.id, 'EXISTS', state.operationsIds.has(op.id), state.operationsKeysMap.get(key)?.timestamp, op.timestamp);
                }
            } else {
                if (!state.operationsIds.has(op.id)) {
                    const parsedTimestamp = Timestamp.parse(op.timestamp);
                    if (parsedTimestamp) {
                        if ('docId' in op && op.docId) {
                            const oldHash = clock.merklesMap[op.docId]?.hash;
                            clock.merklesMap[op.docId] = insert(
                                clock.merklesMap[op.docId] || {},
                                parsedTimestamp,
                                op.type
                            );
                            // logger.log('MERKLE INSERT', op.docId, op.timestamp, op.type, 's', oldHash, clock.merklesMap[op.docId].hash);
                            ClockService.updatedGraphs[op.docId] = (ClockService.updatedGraphs[op.docId] || 0) + 1;
                            if (updateSynced) {
                                state.synced.set(op.docId, Synced.Syncing);
                                state.staleDocs.set(op.docId, (state.staleDocs.get(op.docId) || 0) + 1);
                                state.graphsToSend.set(op.docId, clock.merklesMap[op.docId].hash);
                            }
                        }
                        if ('buyDocId' in op && op.buyDocId) {
                            const oldHash = clock.merklesMap[op.buyDocId]?.hash;
                            clock.merklesMap[op.buyDocId] = insert(
                                clock.merklesMap[op.buyDocId] || {},
                                parsedTimestamp,
                                op.type
                            );
                            // logger.log('MERKLE INSERT', op.buyDocId, op.timestamp, op.type, 'b', oldHash, clock.merklesMap[op.buyDocId].hash);
                            ClockService.updatedGraphs[op.buyDocId] = (ClockService.updatedGraphs[op.buyDocId] || 0) + 1;
                            if (updateSynced) {
                                state.synced.set(op.buyDocId, Synced.Syncing);
                                state.staleDocs.set(op.buyDocId, (state.staleDocs.get(op.buyDocId) || 0) + 1);
                                state.graphsToSend.set(op.buyDocId, clock.merklesMap[op.buyDocId].hash);
                            }
                        }
                    }
                    putOperationInOrder(op, state.operations);
                    state.operationsIds.set(op.id, true);
                    state.opsToSendToWorker.push(op);
                    state.opsToSend.push(op);
                    state.opsToSave.push(op);
                    if (!existingMsg?.timestamp || existingMsg?.timestamp < op.timestamp) {
                        state.operationsKeysMap.set(key, op);
                    }
                }
            }
        }
    });
    // console.log('AFTER merge', operations.length && clock.merklesMap[operations[0].docId]?.hash);
    return state;
}

export function filterUnavailableDocs(state: State, unavailableDocs: string[] = []): State {
    const updatedState: State = {
        ...state,
        documents: {},
        documentsIds: [],
        documentTypes: new Map<string, DocumentType>(),
        positions: {},
        appointments: {},
        complaintNotes: {},
        complaintSpareParts: {},
        documentConnections: {},
        opsToSendToWorker: [],
        opsToSave: [],
        graphsToSend: new Map(),
        deliveries: {},
    };
    const inavailableDocsMap = new Map<string, boolean>();
    unavailableDocs.forEach((docId) => {
        inavailableDocsMap.set(docId, true);
    });
    state.documentsIds.forEach((docId) => {
        if (!inavailableDocsMap.has(docId)) {
            updatedState.documentsIds.push(docId);
            updatedState.documents[docId] = state.documents[docId];
            updatedState.documentTypes.set(docId, state.documentTypes.get(docId));
        } else {
            updatedState.synced.delete(docId);
            updatedState.staleDocs.delete(docId);
        }
    });
    for (const [key, position] of Object.entries(state.positions)) {
        if (!inavailableDocsMap.has(position.documentId)) {
            updatedState.positions[key] = position;
        }
    }
    for (const [key, appointment] of Object.entries(state.appointments)) {
        if (appointment && !inavailableDocsMap.has(appointment.documentId)) {
            updatedState.appointments[key] = appointment;
        }
    }
    for (const [key, note] of Object.entries(state.complaintNotes)) {
        if (note && !inavailableDocsMap.has(note.documentId)) {
            updatedState.complaintNotes[key] = note;
        }
    }
    for (const [key, sparePart] of Object.entries(state.complaintSpareParts)) {
        if (sparePart && !inavailableDocsMap.has(sparePart.documentId)) {
            updatedState.complaintSpareParts[key] = sparePart;
        }
    }
    for (const [key, buyDocumentConnections] of Object.entries(state.documentConnections)) {
        if (!inavailableDocsMap.has(key)) {
            for (const [buyKey, documentConnection] of Object.entries(buyDocumentConnections)) {
                if (!inavailableDocsMap.has(buyKey)) {
                    if (!updatedState.documentConnections[key]) {
                        updatedState.documentConnections[key] = {};
                    }
                    updatedState.documentConnections[key][buyKey] = documentConnection;
                }
            }
        }
    }

    for (const [key, delivery] of Object.entries(state.deliveries)) {
        if (!inavailableDocsMap.has(delivery.documentId)) {
            updatedState.deliveries[key] = delivery;
        }
    }

    return updatedState;
}

export function removeUnavailableDocumentsFromState(state: State, unavailableDocs: string[]) {
    let i = state.operations.length;
    while (i--) {
        const op = state.operations[i];
        if (op.del > ('buyDocId' in op && op.buyDocId ? 1 : 0)) {
            state.operationsIds.delete(state.operations[i].id);
            state.operations.splice(i, 1);
        }
    }

    i = state.documentsIds.length;
    while (i--) {
        if (unavailableDocs.includes(state.documentsIds[i])) {
            state.documentsIds.splice(i, 1);
        }
    }

    for (const docId of unavailableDocs) {
        if (ClockService.getClock().merklesMap[docId]) {
            delete ClockService.getClock().merklesMap[docId];
            delete ClockService.getClock().lockedMap[docId];
            ClockService.deletedDocs.push(docId);
            delete ClockService.updatedGraphs[docId];
        }
        delete state.documents[docId];
        for (const key in state.positions) {
            if (state.positions[key].documentId === docId) {
                delete state.positions[key];
            }
        }

        state.attachments = state.attachments.filter((attachment) => attachment.documentId !== docId);

        for (const key in state.operationsKeysMap) {
            const op = state.operationsKeysMap[key];
            if (op.docId === docId && op.del > ('buyDocId' in op && op.buyDocId ? 1 : 0)) {
                delete state.operationsKeysMap[key];
            }
        }

        for (const key in state.deliveries) {
            if (state.deliveries[key].documentId === docId) {
                delete state.deliveries[key];
            }
        }

        state.synced.delete(docId);
        state.staleDocs.delete(docId);
    }

    for (const docId in state.documentConnections) {
        for (const buyDocId in state.documentConnections[docId]) {
            if (unavailableDocs.includes(docId) || unavailableDocs.includes(buyDocId.replace(/^$/, ''))) {
                for (const uuid in state.documentConnections[docId][buyDocId]) {
                    if (state.documentConnections[docId][buyDocId][uuid].$del > 1) {
                        delete state.documentConnections[docId][buyDocId][uuid];
                    }
                }
                if (Object.keys(state.documentConnections[docId][buyDocId]).length === 0) {
                    delete state.documentConnections[docId][buyDocId];
                }
            }
            if (Object.keys(state.documentConnections[docId]).length === 0) {
                delete state.documentConnections[docId];
            }
        }
    }
}

export function apply(op: Operation, state: State, extraState: ExtraState) {
    const result = operationFunctions[op.type](op, state, extraState);
    let changedDocId = null;
    let changedField = null;
    if (result) {
        changedDocId = result.changedDocId;
        changedField = result.changedField;
        state = result.state;
    }

    if (changedDocId) {
        state = updateDocumentGroups(changedDocId, state, extraState);
        state = documentSummarize(changedDocId, state, extraState);
        state = updateDocumentStatus(changedDocId, changedField, state, extraState);
    }

    return state;
}


export function compareMessages(messages: Operation[], index: Map<string, Operation | PartialOperation>) {
    const existingMessages = new Map<Operation, Operation | PartialOperation>();

    // console.log('messages', messages.length, index.size);
    messages?.forEach((msg1) => {
        const key = msg1.index || operationIndexFunc(msg1);
        const existingMsg = index.get(key);
        existingMessages.set(msg1, existingMsg);
    });

    return existingMessages;
}

export function recompareMessages(existingMessages: Map<Operation, Operation | PartialOperation>, messages: Operation[], localMessages: Operation[]) {
    const index: Map<string, Operation> = new Map();

    for (const msg of localMessages) {
        const key = msg.index || operationIndexFunc(msg);
        if (!index.has(key)) {
            index.set(key, msg);
        }
    }

    messages.forEach((msg1) => {
        const key = msg1.index || operationIndexFunc(msg1);
        const existingMsg = index.get(key);
        const oldExistingMsg = existingMessages.get(msg1);
        if (existingMsg && (!oldExistingMsg || oldExistingMsg.timestamp < existingMsg.timestamp)) {
            existingMessages.set(msg1, existingMsg);
        }
    });

    return existingMessages;
}


function documentSummarize(docId: string, state: State, extraState: ExtraState): State {
    const document = state.documents[docId];
    if (document?.type === 'complaint' || docId.startsWith('c-')) {
        return state;
    }
    const positionsMap = extraState.positionsByDocs.get(docId);
    const positions = positionsMap ? Array.from(extraState.positionsByDocs.get(docId).values()) as SalesDocumentPosition[] : [];
    const summary: Partial<SalesDocument> = {
        priceBeforePromotionsValue: 0,
        priceBeforeDiscountsValue: 0,
        priceAfterDiscountsValue: 0,
        validityDate: null,
        isEmpty: true,
        isInvalid: true,
        partialExternalSource: false,
        hasNonValuatedPositions: false,
        hasOnlyCustomValuatedPositions: false,
    };
    if (document) {
        const documentSummary = DocumentSummarize.summarize(document, positions);

        summary.priceBeforePromotionsValue = documentSummary.totalBeforeDiscountsAndPromotions;
        summary.priceBeforeDiscountsValue = documentSummary.totalAfterPromotions;
        summary.priceAfterDiscountsValue = documentSummary.totalAfterDiscounts;
        summary.pricePromotions = [documentSummary.promotion];
        summary.priceDiscounts = [documentSummary.discount];
        summary.validityDate = documentSummary.validityDate;
        summary.priceIsGross = TaxRatesService.isGross(document.taxRateCalculationMethod);
        if (documentSummary.totalBeforeDiscountsAndPromotions !== document?.priceBeforePromotionsValue
            || documentSummary.totalAfterPromotions !== document?.priceBeforeDiscountsValue
            || documentSummary.totalAfterDiscounts !== document?.priceAfterDiscountsValue
            || documentSummary?.discount !== document?.priceDiscounts?.[0]
            || documentSummary?.promotion !== document?.pricePromotions?.[0]
            || parseDate(documentSummary?.validityDate).getTime() !== parseDate(document?.validityDate).getTime()) {
            document.$dirty = true;
        }
        summary.isEmpty = documentSummary.positionsCount === 0;
        summary.isInvalid = documentSummary.isInvalid;
        summary.partialExternalSource = documentSummary.partialExternalSource;
        summary.hasNonValuatedPositions = documentSummary.hasNonValuatedPositions;
        summary.hasOnlyCustomValuatedPositions = documentSummary.hasOnlyCustomValuatedPositions;
    }
    state.documents[docId] = {
        ...document,
        ...summary,
    };
    return state;
}

function updateDocumentGroups(docId: string, state: State, extraState: ExtraState) {
    const document = state.documents[docId];
    const positionsMap = extraState.positionsByDocs.get(docId);
    const positions = positionsMap ? Array.from(extraState.positionsByDocs.get(docId).values()) : [];
    const groups = GroupingService.grouping(Object.values(groupingSchemas), document, positions);

    state.documents[docId] = {
        ...document,
        groups,
    };
    return state;
}

function updateDocumentStatus(docId: string, changedField: keyof SalesDocument | null, state: State, extraState: ExtraState) {
    const document = state.documents[docId];
    if (document.type === 'sales' && extraState.statusSystems[document.salesDocumentTypeId]?.system?.statusDependsOnFlags) {
        if (changedField === 'statusId') {
            const {documentBuildInFlags, documentFlags} = DocumentStatusService.determineFlags(changedField, document, extraState.statusSystems);
            state.documents[docId] = {
                ...document,
                ...documentBuildInFlags,
                flags: {
                    ...state.documents[docId].flags,
                    ...documentFlags
                },
            };
        } else if (changedField === 'flags' || DocumentBuiltinFlagDict[changedField] || !changedField) {
            const status = DocumentStatusService.determineStatus(changedField, document, extraState.statusSystems);
            if (status) {
                state.documents[docId] = {
                    ...document,
                    statusId: status?.id,
                };
            }
        }
    } else {
        if (!document.statusId && document.type === 'sales') {
            const status = extraState.statusSystems[document.salesDocumentTypeId]?.statuses[0];
            if (status) {
                state.documents[docId] = {
                    ...document,
                    statusId: status?.id,
                };
            }
        }
    }

    return state;
}


export function putOperationInOrder(op: Operation | PartialOperation, operations: (Operation | PartialOperation)[]) {
    const index = operations.findIndex((o) => o.timestamp < op.timestamp);
    if (index === -1) {
        operations.push(op);
    } else {
        operations.splice(index, 0, op);
    }
}

export function putManyOperationsInOrder(ops: (Operation | PartialOperation)[], operations: (Operation | PartialOperation)[]) {
    const merged = [];
    const m = ops.length;
    const n = operations.length;
    let i = 0;
    let j = 0;

    while (i < m && j < n) {
        if (ops[i].timestamp > operations[j].timestamp) {
            merged.push(ops[i]);
            i++;
        } else {
            merged.push(operations[j]);
            j++;
        }
    }

    while (i < m) {
        merged.push(ops[i]);
        i++;
    }

    while (j < n) {
        merged.push(operations[j]);
        j++;
    }
    return merged;
}

export function countGraphsWithAdd(graphs: Record<string, MerkleTrie>) {
    let count = 0;
    for (const key in graphs) {
        if (graphs[key]?.hasAdd) {
            count++;
        }
    }
    return count;
}
